<template>
    <v-app
        v-if="(!$wait.is('load-application') && bootstrap) || cacheInvalidation"
    >
        <div class="ios-top-bar"></div>
        <div class="ios-bottom-bar"></div>
        <div
            class="background"
            :class="{ 'background--error': showUnavailableScreen }"
        ></div>

        <v-dialog v-model="termsDialog" persistent width="500">
            <v-card>
                <v-card-text class="pt-6">
                    <v-expansion-panels tile flat accordion class="mb-4">
                        <v-expansion-panel>
                            <v-expansion-panel-header class="py-1">
                                <div
                                    class="font-weight-bold"
                                    v-html="
                                        $vuetify.lang.t(
                                            '$vuetify.form_items.taxation_documentation_intro_title'
                                        )
                                    "
                                ></div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <div
                                    class="mb-4"
                                    v-html="
                                        $vuetify.lang.t(
                                            '$vuetify.form_items.taxation_documentation_intro'
                                        )
                                    "
                                ></div>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <div
                        class="mb-4"
                        v-if="
                            currentUser &&
                                currentUser.company &&
                                !currentUser.company
                                    .email_taxation_documentation_verified_at &&
                                currentUser.company.email_taxation_documentation
                        "
                    >
                        <div
                            class="mb-2 error--text"
                            v-html="
                                $vuetify.lang.t(
                                    '$vuetify.form_items.taxation_documentation_not_verified_long'
                                )
                            "
                        ></div>

                        <v-btn
                            color="primary"
                            :disabled="whileResendVerification"
                            @click.native.prevent="handleResendVerification"
                        >
                            {{
                                $vuetify.lang.t(
                                    '$vuetify.buttons.resend_verification'
                                )
                            }}
                        </v-btn>
                    </div>
                    <build-form
                        :form-items="items"
                        :actions="actions"
                        :rules="rules"
                        :form.sync="form"
                        ref="form"
                        class="px-2"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-overlay
            :value="!online"
            opacity=".75"
            style="z-index: 9999"
            class="d-flex align-center justify-center text-center"
        >
            <v-icon class="mb-3">mdi-wifi-strength-off</v-icon>
            <p class="body-1">
                no internet connection.
            </p>
        </v-overlay>

        <application-error v-if="showUnavailableScreen" />
        <template v-else>
            <sidebar
                v-if="currentUser"
                :user="currentUser"
                :isOpen="appSidebar"
            />

            <topbar :user="currentUser" />

            <v-content>
                <v-container
                    class="content pa-0 overflow-x-hidden align-start"
                    :class="{ 'fill-height': fillContentHeight }"
                    fluid
                >
                    <v-fade-transition mode="out-in">
                        <router-view></router-view>
                    </v-fade-transition>
                </v-container>

                <v-fade-transition>
                    <easter-egg v-if="requiresAuth" />
                </v-fade-transition>
            </v-content>

            <bottombar
                v-if="showBottomNav && currentUser"
                :user="currentUser"
            />

            <v-snackbar
                v-model="snackbarDisplay"
                :color="snackbar.type"
                :timeout="snackbar.time"
                :top="snackbar.positionY ? snackbar.positionY === 'top' : false"
                :left="
                    snackbar.positionX ? snackbar.positionX === 'left' : false
                "
                :right="
                    snackbar.positionX ? snackbar.positionX === 'right' : false
                "
            >
                <div
                    class="d-flex align-center flex-nowrap"
                    style="width: 100%;"
                >
                    <div v-if="snackbar.image">
                        <v-img
                            :src="snackbar.image"
                            class="mr-2"
                            width="50px"
                            aspect-ratio="1"
                        ></v-img>
                    </div>
                    <div class="mr-2" :class="{ 'ml-3': !snackbar.image }">
                        <template v-if="snackbar.translatedText">
                            {{ snackbar.translatedText }}
                        </template>
                        <template v-if="snackbar.text">
                            {{
                                $vuetify.lang.t(
                                    `$vuetify.snackbar.${snackbar.text}`
                                )
                            }}
                        </template>
                    </div>
                    <div class="ml-auto">
                        <v-btn
                            v-if="snackbar.button"
                            dark
                            text
                            :to="snackbar.button.to"
                        >
                            {{ snackbar.button.text }}
                        </v-btn>
                        <v-btn
                            v-else
                            dark
                            text
                            @click="snackbarDisplay = false"
                        >
                            {{ $vuetify.lang.t('$vuetify.buttons.close') }}
                        </v-btn>
                    </div>
                </div>
            </v-snackbar>
        </template>
        <tours />
    </v-app>
</template>

<script>
import Router from './router/index';
import { mapActions, mapState } from 'vuex';
// components
import InfoCard from './components/Cards/Info';
import Sidebar from './components/App/Sidebar';
import Topbar from './components/App/Topbar';
import Bottombar from './components/App/Bottombar';
import ApplicationError from './components/ApplicationError';
import EasterEgg from './components/EasterEgg';
import UserAvatar from './components/UserAvatar';
import Tours from './components/Tours';
// models
import { User } from './models';
// utils
import { setPageTitle } from './utils/helper';
import {
    LOCAL_STORAGE_LOCALE,
    LOADER_RESEND_VERIFICATION
} from './utils/constants';
import News from '@/models/News';
import StaticPage from '@/models/StaticPage';
import Product from '@/models/Product';
import Pane from './components/Pane.vue';
import BuildForm from './components/Builders/BuildForm.vue';
import {
    email_taxation_documentation,
    terms_and_conditions
} from './utils/formRules';
import Profile from './models/Profile';

export default {
    name: 'App',
    components: {
        BuildForm,
        Pane,
        InfoCard,
        Sidebar,
        Topbar,
        Bottombar,
        ApplicationError,
        EasterEgg,
        UserAvatar,
        Tours
    },
    data() {
        return {
            termsDialog: false,
            online: navigator.onLine,
            snackbarDisplay: false,
            errors: {},
            form: {
                terms_and_conditions: false,
                email_taxation_documentation: ''
            },
            rules: {
                terms_and_conditions,
                email_taxation_documentation
            }
        };
    },
    computed: {
        ...mapState([
            'appSidebar',
            'showUnavailableScreen',
            'bootstrap',
            'cacheInvalidation',
            'init',
            'endpoint',
            'snackbar',
            'show_taxation_documentation_overlay'
        ]),
        whileResendVerification() {
            return this.$wait.is(LOADER_RESEND_VERIFICATION);
        },
        requiresAuth() {
            return this.$route.meta.requiresAuth && this.currentUser;
        },
        showFooterWithLinks() {
            return !!this.$route.meta.showFooterWithLinks;
        },
        showBottomNav() {
            return !!this.$route.meta.showBottomNav;
        },
        fillContentHeight() {
            return !!this.$route.meta.fillContentHeight;
        },
        currentUser() {
            return User.getCurrent();
        },
        items() {
            return [
                {
                    is: 'subhead',
                    name: 'privateSection',
                    label: '$vuetify.profile.company_data',
                    classes: 'mx-2 mb-2'
                },
                {
                    is: 'text',
                    name: 'email_taxation_documentation'
                },
                {
                    is: 'checkbox',
                    label:
                        '$vuetify.form_items.data_protection_and_conditions_of_participation',
                    linkName: '$vuetify.form_items.data_protection_link',
                    linkName2: '$vuetify.form_items.terms_link',
                    slug: 'data_protection',
                    slug2: 'terms',
                    tooltip: '$vuetify.tooltips.new_window',
                    name: 'terms_and_conditions',
                    readonly: this.currentUser
                        ? !!this.currentUser.terms_and_conditions
                        : false,
                    classes: 'mb-4'
                }
            ];
        },
        actions() {
            return [
                {
                    is: 'button',
                    name: 'accept_terms',
                    classes: 'mr-auto',
                    color: 'primary',
                    clickMethod: this.submit
                },
                {
                    is: 'button',
                    name: 'accept_terms_later',
                    color: 'primary',
                    noValidation: true,
                    clickMethod: () => {
                        this.termsDialog = false;
                    }
                }
            ];
        }
    },
    methods: {
        ...mapActions(['set', 'fetchBootstrap', 'fetchInit', 'setSnackbar']),
        async handleResendVerification() {
            const result = await Profile.resendVerificationMail();

            this.setSnackbar({
                positionX: 'left',
                type: 'success',
                translatedText: result.data.message,
                time: 5000
            });

            this.termsDialog = false;
        },
        async submit() {
            let result = null;
            try {
                result = await User.submitChangeCompanyData(this.form);
            } catch (e) {
                this.errors =
                    e.response && e.response.data && e.response.data.error
                        ? e.response.data.error
                        : {};
                return;
            }

            this.setSnackbar({
                positionX: 'left',
                type: 'success',
                translatedText: result.data.message,
                time: 10000
            });

            this.termsDialog = false;
        },
        prefillForm() {
            if (this.currentUser) {
                this.form.terms_and_conditions = !!this.currentUser
                    .terms_and_conditions;
                this.form.email_taxation_documentation = this.currentUser?.company?.email_taxation_documentation;
            }
        },
        showTermsDialogIfNecessary() {
            if (this.show_taxation_documentation_overlay) {
                this.prefillForm();
                this.termsDialog = true;
            } else {
                this.termsDialog = false;
            }
        },
        onPause() {},
        onResume() {
            this.showTermsDialogIfNecessary();
        },
        handleConnection(e) {
            this.online = e.type !== 'offline';
        },
        onDeviceReady() {
            document.addEventListener('pause', this.onPause, false);
            document.addEventListener('resume', this.onResume, false);
        },
        handleTitle(o) {
            if (o.meta.titleType && o.meta.titleType === 'static') {
                document.title = setPageTitle(
                    o.meta.title,
                    StaticPage.getBySlug(o.params.slug).title
                );
            } else if (
                o.meta.title &&
                o.meta.titleType &&
                o.meta.titleType === 'product'
            ) {
                document.title = setPageTitle(
                    o.meta.title,
                    Product.query()
                        .withAll()
                        .find(o.params.productId).title
                );
            } else if (
                o.meta.title &&
                o.meta.titleType &&
                o.meta.titleType === 'news'
            ) {
                document.title = setPageTitle(
                    o.meta.title,
                    News.getDetails(o.params.newsId).headline
                );
            } else if (o.meta.title) {
                document.title = setPageTitle(o.meta.title);
            }
        }
    },
    watch: {
        currentUser(n, o) {
            if (n && o?.email !== n?.email) {
                this.showTermsDialogIfNecessary();
            }
        },
        endpoint(n, o) {
            window.location.reload();
        },
        snackbar() {
            this.snackbarDisplay = true;
        },
        $route(to) {
            this.handleTitle(to);
        }
    },
    mounted() {
        window.addEventListener('online', this.handleConnection);
        window.addEventListener('offline', this.handleConnection);
        // push notifications
        document.addEventListener('deviceready', this.onDeviceReady);
        this.$nextTick(async () => {
            const localLocale = localStorage.getItem(LOCAL_STORAGE_LOCALE);
            if (localLocale) this.$i18n.locale = localLocale;

            if (this.$route.name === 'impersonate') {
                await this.fetchBootstrap();
            } else {
                await this.fetchInit();
                await this.fetchBootstrap();
                this.showTermsDialogIfNecessary();
            }

            this.handleTitle(this.$route);
        });
    },
    beforeDestroy() {
        window.removeEventListener('online', this.handleConnection);
        window.removeEventListener('offline', this.handleConnection);
        document.removeEventListener('deviceready', this.onDeviceReady);
        document.removeEventListener('pause', this.onPause, false);
        document.removeEventListener('resume', this.onResume, false);
    }
};
</script>
